import React, { useState, useEffect } from 'react';
import RequestViewer from './RequestViewer';
import ResponseViewer from './ResponseViewer';
import HttpSearchBar from './HttpSearchBar';
import { buildSearchRegex } from './HighlightedText';

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Calculate total HTTP message size (status line + headers + body)
 */
const calculateTotalSize = (exchange, type) => {
  if (!exchange) return { body: 0, total: 0 };

  const isRequest = type === 'request';
  const data = isRequest ? exchange.request : exchange.response;

  if (!data) return { body: 0, total: 0 };

  // Body size
  const bodySize = data.body?.length || 0;

  // Headers size (name + ": " + value + "\r\n" for each)
  const headersSize = (data.headers || []).reduce((sum, h) =>
    sum + h.name.length + 2 + h.value.length + 2, 0
  );

  // Status/method line
  const firstLineSize = isRequest
    ? (data.method?.length || 0) + (data.url?.length || 0) + 15  // "GET /path HTTP/1.1\r\n"
    : (data.statusCode ? 15 : 0) + (data.statusText?.length || 0) + 2;  // "HTTP/1.1 200 OK\r\n"

  return {
    body: bodySize,
    total: firstLineSize + headersSize + 2 + bodySize  // +2 for blank line separator
  };
};

/**
 * ExchangePanel - Displays HTTP request/response with top-level tabs, search, and size info.
 * Wraps RequestViewer/ResponseViewer with enhanced UX.
 */
const ExchangePanel = ({
  exchange,
  title,
  titleColor,
  isDarkMode,
  topLevelTab: topLevelTabProp,
  onTopLevelTabChange,
  viewMode,
  onViewModeChange
}) => {
  const [topLevelTabState, setTopLevelTabState] = useState('request'); // 'request' | 'response'
  const topLevelTab = topLevelTabProp || topLevelTabState;
  const setTopLevelTab = (t) => (onTopLevelTabChange ? onTopLevelTabChange(t) : setTopLevelTabState(t));
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOptions, setSearchOptions] = useState({ caseSensitive: false, useRegex: false });
  const [searchRegex, setSearchRegex] = useState(null);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [requestMatchCount, setRequestMatchCount] = useState(0);
  const [responseMatchCount, setResponseMatchCount] = useState(0);

  // Keyboard handler for Ctrl+F
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setSearchVisible(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update search regex when query or options change
  const handleSearchChange = (query, options) => {
    setSearchQuery(query);
    setSearchOptions(options);
    const regex = buildSearchRegex(query, options);
    setSearchRegex(regex);
    setActiveMatchIndex(0);
  };

  const totalMatches = topLevelTab === 'request' ? requestMatchCount : responseMatchCount;

  const handleNext = () => {
    if (totalMatches > 0) {
      setActiveMatchIndex((prev) => (prev + 1) % totalMatches);
    }
  };

  const handlePrev = () => {
    if (totalMatches > 0) {
      setActiveMatchIndex((prev) => (prev - 1 + totalMatches) % totalMatches);
    }
  };

  // Calculate sizes for current tab
  const { body, total } = topLevelTab === 'request'
    ? calculateTotalSize(exchange, 'request')
    : calculateTotalSize(exchange, 'response');

  if (!exchange) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          No exchange data available
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Panel Header: Title | Status | Size */}
      <div className={`px-3 py-2 border-b font-semibold text-xs flex items-center justify-between sticky top-0 z-10 ${
        isDarkMode ? `border-gray-700 bg-gray-800 ${titleColor}` : `border-gray-200 bg-gray-50 ${titleColor}`
      }`}>
        <span>{title}</span>
        <div className="text-[10px] font-normal text-gray-500 flex items-center gap-3">
          <span>Status: {exchange.response?.statusCode || 'N/A'}</span>
          <span>Size: {formatBytes(body)} / {formatBytes(total)}</span>
        </div>
      </div>

      {/* Top-Level Tabs: Request | Response */}
      <div className={`px-3 py-1.5 border-b flex items-center gap-1 ${
        isDarkMode ? 'border-gray-700 bg-gray-850' : 'border-gray-200 bg-gray-100'
      }`}>
        {['request', 'response'].map(tab => (
          <button
            key={tab}
            onClick={() => setTopLevelTab(tab)}
            className={`px-3 py-1 text-xs rounded font-medium transition-colors ${
              topLevelTab === tab
                ? (isDarkMode ? 'bg-amber-600 text-white' : 'bg-amber-600 text-white')
                : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      {searchVisible && (
        <HttpSearchBar
          isDarkMode={isDarkMode}
          onSearchChange={handleSearchChange}
          totalMatches={totalMatches}
          activeMatchIndex={activeMatchIndex}
          onNext={handleNext}
          onPrev={handlePrev}
          onClose={() => setSearchVisible(false)}
          visible={searchVisible}
        />
      )}

      {/* Content Area: Delegate to RequestViewer or ResponseViewer */}
      <div className="flex-1 overflow-hidden">
        {topLevelTab === 'request' ? (
          <RequestViewer
            exchange={exchange}
            isDarkMode={isDarkMode}
            searchRegex={searchRegex}
            activeMatchIndex={activeMatchIndex}
            matchOffset={0}
            onMatchCount={setRequestMatchCount}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        ) : (
          <ResponseViewer
            exchange={exchange}
            isDarkMode={isDarkMode}
            searchRegex={searchRegex}
            activeMatchIndex={activeMatchIndex}
            matchOffset={0}
            onMatchCount={setResponseMatchCount}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        )}
      </div>
    </div>
  );
};

export default ExchangePanel;
